import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { FormGroup } from "@angular/forms";
import { AuthService } from "app/auth/auth.service";
import {
  Events,
  MenuController,
  ModalController,
  Platform,
  PopoverController,
} from "ionic-angular";
import SortOptionsMedications from "models/content-wrapper/sort-options-medications";
import { Action } from "models/order-management/models";
import * as moment from "moment-timezone";
import { MedicationSearchResults } from "pages/patient-chart/medications/patient-medication";
import { OrderManagementDataService } from "providers/order-management-data-service/order-management-data-service";
import { PatientAllergiesService } from "providers/patient-charts/patient-allergies-service";
import { PatientMedicationsService } from "providers/patient-charts/patient-medications.service";
import { ResourceManagementService } from "providers/resource-management/resource-management.service";
import { UtilityService } from "providers/utility-service/utility-service";
import { Observable, Subject, Subscription } from "rxjs";
import { take } from "rxjs/operators";
import Utils from "utils/Utilities";
import { CertificationService } from "../../../certification/services/certification-service";
import { mapCBMemberToMemberDetails } from "../../../mappers/community-bereavement/member-details-mapper";
import CalendarEventVisitStatus from "../../../models/calendar/calendar-event-visit-status";
import CalendarPatient from "../../../models/calendar/calendar-patient";
import { Member } from "../../../models/community-bereavement/member";
import CollapsibleEvent from "../../../models/events/collapsible-modal.event";
import BenefitPeriod from "../../../models/patient-chart/benefit-period";
import CareLocation from "../../../models/patient-chart/care-location";
import LookupValue from "../../../models/patient-chart/lookupValue";
import PatientChartBrief from "../../../models/patient-chart/patient-chart-brief";
import Toggle from "../../../models/toggle";
import { disciplineList } from "../../../pages/calendar/discipline-legend";
import {
  AppStatus,
  ConnectionStatusEnum,
} from "../../../providers/app-status/app-status";
import { CareTeamsService } from "../../../providers/care-teams-service/care-teams-service";
import { CommunityBereavementServiceProvider } from "../../../providers/community-bereavement-service/community-bereavement-service";
import { ConfigurationService } from "../../../providers/configuration/configuration.service";
import { MobileUtilsProvider } from "../../../providers/mobile-utils/mobile-utils";
import { NavBarServiceProvider } from "../../../providers/nav-bar-service/nav-bar-service";
import { PatientDataManager } from "../../../providers/offline-manager/patient-data-manager";
import { OrderDataService } from "../../../providers/order-data-service/order-data-service";
import { ReferralsServiceProvider } from "../../../providers/patient-charts/referrals-service";
import { PlanOfCareDataService } from "../../../providers/poc-data-service/poc-data-service";
import { ReportExecutionService } from "../../../providers/report-execution-service/report-execution.service";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import * as utils from "../../../utils";
import { NavigationService } from "../../navigation.service";
import { NavigationDataService } from "../../services/navigation-data.service";
import { AllergiesPopover } from "../allergies-popover/allergies-popover";
import { CBMemberData } from "../brief-bar/cb-member-data";
import { DeclineDashboardPopover } from "../decline-dashboard-popover/decline-dashboard-popover";

const HOSPICE_PATIENT_CARE_TYPE = "Hospice";

@Component({
  selector: "calendar-brief-bar",
  templateUrl: "calendar-brief-bar.html",
})
export class CalendarBriefBarComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  isLoading: boolean = true;
  useExternalId: boolean = false;
  patientChartBriefSubscription: Subscription; // Observable<PatientChartBrief>;
  patientBiometricsSubscription: Subscription;
  palliativeSubscription: Subscription;
  subscriptions: Subscription = new Subscription();
  careTeamSubscription: Subscription;
  patientChartFeatureSubscription: Subscription;
  patientChartBrief: PatientChartBrief;
  chartId: string;
  patientChartId: number = 0;
  patientId: string;
  assessmentId: string;
  assessmentUuid: string;
  patientData: object = {};
  dareStatus: string = null;
  hasError: boolean = false;
  isNewPatientChart: boolean = false;
  _numOpenOrders = 0;
  appSyncStatus: ConnectionStatusEnum;
  connectionStatusEnum = ConnectionStatusEnum;
  isPatientChart: boolean = false;
  smSubscription: any;
  showPlanOfCare: boolean = true;
  showPatientBiometrics: boolean = true;
  briefDataReady = new Subject<Boolean>();
  briefDataReady$: Observable<Boolean>;
  _patientProfileUpdatedSubscription;
  _patientChartDiagnosisUpdatedSubscription;
  _patientChartLevelOfCareUpdatedSubscription;
  allergyLists = [];

  // Community Bereaved variables
  memberDetails: CBMemberData = null;
  member: Member = null;
  memberId: number = 0;
  memberStatus: String = "";
  serviceDate: String = "";
  orgTimezone: string;

  communityBereavedFeatureSubscription: Subscription;
  updateCBMemberSubscription: Subscription;

  ctiV2Toggle: boolean;
  analyticsToggle: boolean = false;
  palliativeV1_5Toggle: boolean = false;
  benefitPeriodStartDate: string;
  benefitPeriodEndDate: string;
  currentBenefitPeriodNumber: string;
  userDisplayName: string;

  // adding new parameters for brief-bar migration
  @Input() patientCareType: String = HOSPICE_PATIENT_CARE_TYPE;
  protected readonly legend = disciplineList;
  @Input() isOMActive!: boolean;
  @Input() hasCalendarPagePermission: boolean;
  @Input() showVisitStatus!: boolean;
  @Input() fg: FormGroup;
  @Input() eventTimeFilter!: CalendarEventVisitStatus[];
  @Input() showLegend!: boolean;
  @Input() showPatientDetails: boolean = false;
  @Input() showHUVNotification: boolean = false;

  @Input()
  showOrderManagement: boolean = false;
  @Input()
  patientChartIdProviderPanel: number;

  @Input()
  showAsCBBriefBar: boolean = false;

  @Input() set numOpenOrders(numOpenOrders) {
    this._numOpenOrders = numOpenOrders;
  }

  @Input() set qbosId(qbosId) {
    if (qbosId && qbosId > 0) {
      this.patientChartId = qbosId;
      this.useExternalId = false;
      this.loadPatientChartBrief();
    } else {
      this.patientChartId = 0;
      this.patientChartBrief = null;
    }
  }

  @Input() set cbMemberId(memberData: Member) {
    if (memberData) {
      this.memberId = memberData.id;
    }
    this.member = memberData;
    this.loadCBMemberBrief();
  }

  @Input() selectedPatients!: CalendarPatient[];

  constructor(
    private planOfCareDataService: PlanOfCareDataService,
    public navSvc: NavigationService,
    public navData: NavigationDataService,
    public plt: Platform,
    public stateData: StateManagementService,
    public orderSvc: OrderDataService,
    public events: Events,
    public appStatus: AppStatus,
    public mobileUtilsProvider: MobileUtilsProvider,
    public menuCtrl: MenuController,
    public navBarServiceProvider: NavBarServiceProvider,
    public referralService: ReferralsServiceProvider,
    private careTeamService: CareTeamsService,
    private configurationService: ConfigurationService,
    private patientDataManager: PatientDataManager,
    private popoverCtrl: PopoverController,
    private modalCtrl: ModalController,
    private communityBereavementService: CommunityBereavementServiceProvider,
    public allergySvc: PatientAllergiesService,
    private authService: AuthService,
    private certificationService: CertificationService,
    private dataService: ReportExecutionService,
    private medicationService: PatientMedicationsService,
    private orderManagementDataService: OrderManagementDataService,
    private resourceManagementService: ResourceManagementService,
    private utilityService: UtilityService,
    private authSvc: AuthService
  ) {
    // this.patientCareType = this.stateData.patientCareType;
    this.appSyncStatus = this.appStatus.getAppStatus();
    this.briefDataReady$ = this.briefDataReady.asObservable();
    this.events.subscribe("networkchange", (eventData) => {
      this.appSyncStatus = eventData.status;
    });
    this.stateData.showAnalytics.subscribe((x) => {
      if (x !== null) {
        this.analyticsToggle = x;
      }
    });
    this.stateData.ctiV2.subscribe((x) => {
      if (x !== null) {
        this.ctiV2Toggle = x;
      }
    });
  }

  getUserDisciplines() {
    this.stateData.checkUserDisciplines().subscribe(
      (data) => {
        if (data["persons"] && data["persons"].length > 0) {
          let userDisciplines = data["persons"][0]["discipline"] === "HA";
          if (userDisciplines) {
            this.showOrderManagement = false;
          }
        }
      },
      (error) => {}
    );
  }

  ngOnInit(): void {
    if (this.patientChartId) {
      this.loadPatientChartBrief();
    }
    if (this.showAsCBBriefBar) {
      if (!this.memberId || this.memberId === 0) {
        this.events.subscribe("memberIdChange", (data) => {
          if (data && data.id) {
            this.memberId = data.id;
            this.loadCBMemberBrief();
          }
        });
      }

      this.loadCBMemberBrief();

      this.updateCBMemberSubscription = this.stateData
        .getCBMemberObserver()
        .subscribe((data: Member) => {
          this.member = data;
          this.loadCBMemberBrief();
        });
    } else {
      this.palliativeSubscription = this.stateData.showPalliativeV1_5.subscribe(
        (x) => {
          if (x !== null) {
            this.palliativeV1_5Toggle = x;
            this.loadPatientChartBrief();
          }
        }
      );
      this.patientBiometricsSubscription =
        this.stateData.showPatientBiometrics.subscribe((x) => {
          this.showPatientBiometrics = x;
        });

      if (this.stateData.getAssessmentId()) {
        this.assessmentId = this.stateData.getAssessmentId();
      } else {
        this.stateData.getAssessmentIdObserver().subscribe((data) => {
          this.assessmentId = data;
        });
      }

      if (this.stateData.getAssessmentUuid()) {
        this.assessmentUuid = this.stateData.getAssessmentUuid();
      } else {
        this.stateData.getAssessmentUuidObserver().subscribe((data) => {
          this.assessmentUuid = data;
        });
      }

      this.getUserDisciplines();
      if (this.authService.getUser()) this.getUserDisplayName();
    }
  }

  ngAfterViewInit() {
    // Using the 'AfterViewInit' lifecycle hook because when we destroy existing brief-bar instance and load a new brief-bar instance (when changing tabs),
    // the NEW brief-bar's onInit() is invoked first, and then the existing brief-bar instance's onDestroy() invoked second.
    // This messes up the event subscription/unsubscription order.
    // Putting the event subscribtion in this lifecycle hook prevents this from happening.
  }

  ionViewWillLeave() {
    if (this.smSubscription) this.smSubscription.unsubscribe();
  }

  ngOnDestroy() {
    // NOVA-1490 (SUP-19458): Removed all sessionStorage patient key clearing from here.
    // ngOnDestroy fires on ANY setRoot navigation — including within-patient navigations
    // (Calendar → Order Entry, Calendar → Visit). Clearing keys here creates a partial
    // state that scrambles patient context on subsequent navigations.
    // Patient context cleanup is now handled by the navigation exit points:
    // user-popover.ts, dashboard-navigation-bar.ts, apps-popover.ts,
    // and navigation-header.ts (sendDashboard).
    this.subscriptions.unsubscribe();

    if (this.patientBiometricsSubscription) {
      this.patientBiometricsSubscription.unsubscribe();
    }

    if (this.smSubscription) {
      this.smSubscription.unsubscribe();
    }
    if (this.careTeamSubscription) {
      this.careTeamSubscription.unsubscribe();
    }
    if (this.patientChartFeatureSubscription) {
      this.patientChartFeatureSubscription.unsubscribe();
    }

    if (this.patientChartBriefSubscription) {
      this.patientChartBriefSubscription.unsubscribe();
    }

    if (this.palliativeSubscription) {
      this.palliativeSubscription.unsubscribe();
    }

    if (this.palliativeSubscription) {
      this.palliativeSubscription.unsubscribe();
    }

    this.events.unsubscribe("networkchange");

    // The following three ionic events are specific to brief-bar, so let's unsubscribe from them when component is destroyed.
    // (Not unsubscribing was causing the brief bar and global header update issues...
    // ... as it was updating brief-bar/global patient details based on previously-viewed patients, in turn affecting the dynamic styling).
    if (this._patientProfileUpdatedSubscription) {
      this.events.unsubscribe(
        "patientProfileUpdated",
        this._patientProfileUpdatedSubscription
      );
      this._patientProfileUpdatedSubscription = undefined;
    }
    if (this._patientChartDiagnosisUpdatedSubscription) {
      this.events.unsubscribe(
        "patientChartDiagnosisUpdated",
        this._patientChartDiagnosisUpdatedSubscription
      );
      this._patientChartDiagnosisUpdatedSubscription = undefined;
    }
    if (this._patientChartLevelOfCareUpdatedSubscription) {
      this.events.unsubscribe(
        "patientChartLevelOfCareUpdated",
        this._patientChartLevelOfCareUpdatedSubscription
      );
      this._patientChartLevelOfCareUpdatedSubscription = undefined;
    }

    if (this.showAsCBBriefBar) {
      this.communityBereavedFeatureSubscription.unsubscribe();
      this.updateCBMemberSubscription.unsubscribe();
      this.events.unsubscribe("memberIdChange");
      // uncomment if we add a call to the back-end to pull brief bar details below
      //      this.communityBereavedBriefSubscription.unsubscribe();
    }
  }

  loadOpenOrders() {
    if (this.showOrderManagement) {
      if (this.stateData.getSectionId() !== "orderEntry") {
        if (this.patientChartBrief) {
          this.orderSvc
            .getOpenOrdersCount(this.patientChartBrief.externalId)
            .pipe()
            .take(1)
            .subscribe((data) => {
              this._numOpenOrders = data.openOrdersCount;
            });
        }
      }
    }
  }

  loadAllergies() {
    // his.increaseSpinnerCount("Saving ...");
    this.allergySvc
      .getAllergies(this.patientChartId)
      .pipe()
      .take(1)
      .subscribe((data) => {
        this.allergyLists = data.patientAllergy;
        this.allergySvc.showAllergyHeaderFlag = false;
      });
  }

  /**
   * Loads the patient chart brief bar data from referral service
   */
  loadPatientChartBrief(): void {
    this.patientChartFeatureSubscription = this.stateData.isPatientChart
      .pipe()
      .take(1)
      .subscribe((x) => {
        this.isNewPatientChart = x;
        if (!this.patientChartId || this.patientChartId === 0) {
          let chartId =
            this.patientChartIdProviderPanel > 0
              ? {
                  patientChartId: this.patientChartIdProviderPanel,
                  externalId: true,
                }
              : this.navBarServiceProvider.parsePatientChartIdsFromUrl(
                  window.location.hash
                );
          this.patientChartId = chartId.patientChartId;
          this.useExternalId = chartId.externalId;
        }

        if (this.patientChartId && this.patientChartId !== 0) {
          // wild hack but in some cases the feature toggle flag isn't working
          if (!this.isNewPatientChart && this.patientChartId > 199999) {
            this.isNewPatientChart = true;
          }

          if (this.isOffline()) {
            this.patientChartBriefSubscription = this.patientDataManager
              .getPatientProfile(this.patientChartId)
              .subscribe(
                (x) => {
                  this.patientChartBrief = x;
                  this.remapPatientChartBrief(x);
                  this.isLoading = false;
                },
                (error) => {
                  this.isLoading = false;
                }
              );
          } else {
            const toggle = new Toggle();
            toggle.showPalliativeV1_5 = this.palliativeV1_5Toggle;
            const isHisRequest = Boolean(
              window.sessionStorage.getItem("isHisRequest")
            );
            // Request-identity guard: drop late responses whose patient id no
            // longer matches the currently selected patient.
            const requestPatientChartId = this.patientChartId;
            this.patientChartBriefSubscription = this.referralService
              .getPatientChartBrief(
                this.patientChartId,
                isHisRequest ? true : this.useExternalId,
                this.isNewPatientChart,
                toggle
              )
              .subscribe(
                (x) => {
                  if (this.patientChartId !== requestPatientChartId) {
                    return;
                  }
                  this.patientChartBrief = x;
                  this.patientCareType = x.careType;
                  this.serviceDate = this.mapPatientServiceDates(x);
                  // this.stateData.updatePatientCareType(x.careType);

                  this.stateData.updateSelectedPatient(x);
                  this.loadOpenOrders();
                  this.loadAllergies();
                  this.loadCurrentBenefitPeriod(
                    this.patientChartBrief.externalId,
                    x
                  );
                  this.isLoading = false;
                  window.sessionStorage.removeItem("isHisRequest");
                },
                (error) => {
                  if (this.patientChartId !== requestPatientChartId) {
                    return;
                  }
                  this.isLoading = false;
                  window.sessionStorage.removeItem("isHisRequest");
                }
              );
          }
        } else {
          this.isLoading = false;
          window.sessionStorage.removeItem("isHisRequest");
        }
      });
  }

  remapPatientChartBrief(x: any) {
    if (x.patientGender) {
      let lookupValue = new LookupValue();
      if (x.patientGender === "Female") {
        lookupValue.id = 2;
        lookupValue.description = "Female";
        this.patientChartBrief.profileImagePath = "assets/mantis/female.jpg";
      } else if (x.patientGender === "Male") {
        lookupValue.id = 1;
        lookupValue.description = "Male";
        this.patientChartBrief.profileImagePath = "assets/mantis/male.jpg";
      }
      this.patientChartBrief.gender = lookupValue;
    }
    let fullName: string = x.patientFullName;
    this.patientChartBrief.firstName = fullName.substr(
      0,
      fullName.indexOf(" ")
    );
    this.patientChartBrief.lastName = fullName.substr(
      fullName.indexOf(" ") + 1
    );
    this.patientChartBrief.birthDate = x.dateOfBirth;
    this.patientChartBrief.age = x.age;
    this.patientChartBrief.primaryDiagnosisDescription = x.primaryDiagnosis;
    this.patientChartBrief.secondaryDiagnosisDescription = x.secondaryDiagnosis;
    this.patientChartBrief.attendingPhysicianName =
      x.attendingPhysicianFullName;
    this.patientChartBrief.primaryCareGiverName = x.primaryCareGiverFullName;
    this.patientChartBrief.primaryCareGiverPhoneNumber =
      x.primaryCareGiverPhoneNumber;
    this.patientChartBrief.externalId = x.chartId;
    let careLocation: CareLocation = {
      type: x.careLocationType,
      name: "",
      address1: x.careLocationAddress1,
      address2: x.careLocationAddress2,
      city: x.careLocationCity,
      state: x.careLocationState,
      zipCode: x.careLocationZip,
    };
    this.patientChartBrief.careLocation = careLocation;
    this.patientChartBrief.foodAllergies = x.foodAllergies;
    this.patientChartBrief.drugAllergies = x.drugAllergies;
    this.patientChartBrief.codeStatus = x.codeStatus;
    this.patientChartBrief.riskPriority = x.riskPriority;
    let benefitPeriod: BenefitPeriod = {
      currentBenefitPeriod: x.benefitPeriod,
      currentBenefitPeriodStartDate: new Date(),
      currentBenefitPeriodEndDate: new Date(),
    };
    this.patientChartBrief.benefitPeriod = benefitPeriod;
    this.dareStatus = x.dareStatus;
  }

  sendToPlanOfCare() {
    let externalId = this.patientChartId
      ? this.patientChartId
      : this.getExternalId();
    this.stateData.setSectionId(null);
    this.planOfCareDataService.setPatientChartId(externalId.toString());
    this.planOfCareDataService.setAssessmentId(this.assessmentId);
    window.localStorage.setItem("previousPageUrl", "#/calendar");
    window.localStorage.setItem(
      "calendarInfo",
      JSON.stringify({ patChartId: this.getExternalId() })
    );

    let assesmentId = this.assessmentId || 0;
    window.location.href =
      window.location.href.split("#")[0] +
      "#/patient/" +
      externalId +
      "/planOfCare/" +
      assesmentId;
  }

  sendToOrderEntry() {
    let externalId = this.patientChartId
      ? this.patientChartId
      : this.getExternalId();
    this.stateData.setSectionId(null);
    this.planOfCareDataService.setPatientChartId(externalId.toString());
    this.planOfCareDataService.setAssessmentId(this.assessmentId);
    this.navSvc.goToInternalPage(
      this.navBarServiceProvider.orderEntryOMFeatureToggleOn()
        ? "OrderEntryOMPage"
        : "OrderEntryPage",
      {
        patChartId: externalId,
        assessmentId: this.assessmentId || null,
        assessmentUuid: this.assessmentUuid || null,
      }
    );
  }

  showOrderEntry() {
    return this.isOffline()
      ? false
      : this.stateData.getSectionId() !== "orderEntry";
  }

  isOffline() {
    return this.appSyncStatus === this.connectionStatusEnum.Offline;
  }

  getExternalId(): Number {
    //based on de actual conditions it returns the patientChartId, for future references it was returning window.sessionStorage.getItem("externalId")
    return +window.sessionStorage.getItem("externalId");
  }

  downloadReports() {
    if (this.isOrderEntryOMActive()) {
      this.downloadNewMedProfileReport();
    } else {
      this.downloadMedicationProfile();
    }
  }

  downloadNewMedProfileReport() {
    const user = this.authSvc.getUser();
    // const role = JSON.parse(user.profile.role);
    // const { orgID } = role[0];
    let data = {
      format: "pdf",
      parameters: {
        pOrgID: user.tenantId,
        pPatientChartID: this.navBarServiceProvider.getExternalId(),
      },
    };
    let reportName = `MedicationProfile_Patient_${this.navBarServiceProvider.getExternalId()}.pdf`;
    this.dataService.downloadReport(
      "om_medication_profile_detail_report",
      data,
      reportName,
      "application/pdf"
    );
    this.trackOrders();
  }

  downloadMedicationProfile() {
    //UNICORNS-2 - Download medication report required two parameters
    //QbosId and PatientId beacuase of feature toggle.
    let QBOSId = this.navBarServiceProvider.getExternalId()
      ? this.navBarServiceProvider.getExternalId()
      : this.stateData.getChartId();
    let chartId = this.navBarServiceProvider.getPatientChartId()
      ? this.navBarServiceProvider.getPatientChartId()
      : 0;

    this.orderSvc.generateMedReport(QBOSId, chartId).subscribe((data) => {
      let filename = `MedicationProfile_Patient_${QBOSId}.pdf`;
      utils.printDoc(data, filename);
    });
  }

  trackOrders() {
    const sf = {
      fromDate: "",
      fromRow: 0,
      searchString: "",
      source: "Order",
      status: "",
      toDate: "",
      toRow: 999,
      showDiscontinued: true,
    };
    const currentDate = moment(new Date()).format("YYYY-MM-DD");
    this.medicationService
      .getMedications(
        this.referralService.PATIENT_ID,
        sf,
        new SortOptionsMedications("", "", "", "", "", "", "", "", "", ""),
        currentDate
      )
      .pipe(take(1))
      .subscribe(
        (medOrders: MedicationSearchResults) => {
          if (medOrders.patientMedication.length > 0) {
            this.orderManagementDataService
              .bulkUpdateOrders(
                Utils.createTrackOrdersPayload(
                  medOrders.patientMedication.map((p) => ({
                    orderId: p.orderDetails.orderManagementOrderId,
                    medicationId: p.entityId,
                  })),
                  Action.ActionTypeEnum.Printed,
                  moment.tz.guess(),
                  this.userDisplayName
                )
              )
              .subscribe(
                () => {},
                () => {}
              );
          }
        },
        () => {}
      );
  }

  getUserDisplayName() {
    this.resourceManagementService
      .getProviderByExternalId(this.authSvc.getUser().userId)
      .subscribe((provider) => {
        this.userDisplayName = provider.findDisplayName(new Date());
      });
  }

  loadCBMemberBrief(): void {
    this.communityBereavedFeatureSubscription =
      this.stateData.showCommunityBereavement
        .pipe()
        .take(1)
        .subscribe((x) => {
          if (this.member && this.memberId && this.memberId !== 0) {
            if (this.isOffline()) {
              console.log(
                "We are not currently supporting Community Bereaved in the offline mode."
              );
              this.isLoading = false;
            } else {
              this.memberDetails = mapCBMemberToMemberDetails(this.member);
              this.memberStatus = this.memberDetails.isActive
                ? "Activated"
                : "Deactivated";
              this.isLoading = false;

              // publish event to show magenta brief-bar
              this.patientCareType = "CBMember";
              this.stateData.updatePatientCareType("CBMember");
            }
          } else {
            this.isLoading = false;
          }
        });
  }

  getMemberId(): Number {
    if (this.memberId && this.memberId !== 0) {
      return this.memberId;
    } else {
      //@TODO: SMS - is this needed as backup?  Can NavBar Service provide this?
      //      let memberId = this.navBarServiceProvider.getMemberId();
      //      return memberId;
    }
  }

  sendToMemberPage() {
    this.navSvc.goToInternalPage("CommunityBereavementProfilePage", {
      memberId: this.memberId,
    });
  }

  isAllergyActive(): Boolean {
    return this.navBarServiceProvider.patientAllergiesFeatureToggleOn();
  }

  isOrderEntryOMActive(): Boolean {
    return this.navBarServiceProvider.orderEntryOMFeatureToggleOn();
  }

  isPalliative(): Boolean {
    return (
      this.patientChartBrief &&
      this.patientCareType &&
      this.patientCareType.toLowerCase() === "palliative"
    );
  }

  isEvaluation(): Boolean {
    return (
      this.patientChartBrief &&
      this.patientCareType &&
      this.patientCareType.toLowerCase() === "evaluation"
    );
  }

  presentAlertsPopover($event) {
    let modal = this.modalCtrl.create(
      "BiometricsPage",
      { patientChartId: this.navBarServiceProvider.getExternalId() },
      { cssClass: "biometrics-modal" }
    );
    modal.present();
  }

  showAllergries() {
    this.loadAllergies();
    let popover = this.modalCtrl.create(
      AllergiesPopover,
      {
        patientChartId: this.patientChartId,
        allergyLists: this.allergyLists,
      },
      { cssClass: "allergies-modal" }
    );
    popover.present({});
    popover.onDidDismiss((mode) => {
      this.allergySvc.showAllergyHeaderFlag = true;
    });
  }

  showDeclineDashboard() {
    this.loadAllergies();
    let popover = this.modalCtrl.create(
      DeclineDashboardPopover,
      {
        patientChartId: this.patientChartId,
      },
      { cssClass: "decline-dashboard-modal" }
    );
    popover.present({});
    popover.onDidDismiss((mode) => {
      this.allergySvc.showAllergyHeaderFlag = true;
    });
  }

  loadCurrentBenefitPeriod(externalId: number, brief: PatientChartBrief) {
    this.subscriptions.add(
      this.stateData.ctiV2.subscribe((ctiToggle) => {
        if (ctiToggle) {
          let currentDateForBenefitPeriod = moment()
            .local(true)
            .format("YYYY-MM-DD");

          if (this.referralService && this.referralService.PATIENT_STATUS) {
            const patientStatus = this.referralService.PATIENT_STATUS;
            if (
              patientStatus === "discharge" &&
              brief.serviceDate.dischargedDatetime
            ) {
              currentDateForBenefitPeriod = this.getUserTimeMMDDYYYY(
                brief.serviceDate.dischargedDatetime,
                brief.serviceDate.dischargedDatetimeUtc,
                "YYYY-MM-DD"
              );
            }
          }

          this.certificationService
            .GetCurrentBenefitPeriod(externalId, currentDateForBenefitPeriod)
            .pipe()
            .take(1)
            .subscribe(
              (benefitPeriod: any) => {
                if (benefitPeriod) {
                  this.benefitPeriodEndDate = benefitPeriod["endDate"]
                    ? benefitPeriod["endDate"]
                    : "";
                  this.benefitPeriodStartDate = benefitPeriod["startDate"]
                    ? benefitPeriod["startDate"]
                    : "";
                  this.currentBenefitPeriodNumber = benefitPeriod[
                    "benefitPeriod"
                  ]
                    ? benefitPeriod["benefitPeriod"]
                    : "";
                }
              },
              (error) => {
                console.error(error);
              }
            );
        }
      })
    );
  }

  mapPatientServiceDates(brief: PatientChartBrief): String {
    this.orgTimezone = this.authSvc.getUser().timeZoneOrg
      ? this.authSvc.getUser().timeZoneOrg
      : moment.tz.guess();
    if (brief.serviceDate) {
      if (
        brief.serviceDate.admittedDatetime &&
        !brief.serviceDate.dischargedDatetime
      ) {
        this.serviceDate = this.getUserTimeMMDDYYYY(
          brief.serviceDate.admittedDatetime,
          brief.serviceDate.admittedDatetimeUtc,
          "MM/DD/YYYY"
        );
      } else if (
        brief.serviceDate.admittedDatetime &&
        brief.serviceDate.dischargedDatetime
      ) {
        this.serviceDate =
          this.getUserTimeMMDDYYYY(
            brief.serviceDate.admittedDatetime,
            brief.serviceDate.admittedDatetimeUtc,
            "MM/DD/YYYY"
          ) +
          " to " +
          this.getUserTimeMMDDYYYY(
            brief.serviceDate.dischargedDatetime,
            brief.serviceDate.dischargedDatetimeUtc,
            "MM/DD/YYYY"
          );
      }
    }
    return this.serviceDate;
  }

  getUserTimeMMDDYYYY(
    date: string,
    utcDate: string,
    dateFormat: string
  ): string {
    let tempDate;
    if (this.orgTimezone && utcDate) {
      tempDate = moment(utcDate);
      tempDate = tempDate.tz(this.orgTimezone);
    } else {
      // Utc Offset is needed to preserve current behavior without Z
      tempDate = moment(date);
      tempDate = tempDate.utcOffset(0, false).local(true);
    }
    return tempDate.format(dateFormat);
  }

  onCollapseClicked($event: CollapsibleEvent) {
    if ($event.controlId === "visit-status") {
      this.showVisitStatus = $event.isOpen;
    } else if ($event.controlId === "legend") {
      this.showLegend = $event.isOpen;
    }
  }

  showUnassigned() {
    this.navSvc.goToInternalPage("CalendarGridView", {});
  }

  goToPatientProfile() {
    this.referralService.PATIENT_ID = null;
    this.referralService.CARE_TYPE = null;
    this.navSvc.goToPatientProfile(this.getExternalId());
  }
}



// WEBPACK FOOTER //
// ./src/navigation/components/calendar-brief-bar/calendar-brief-bar.ts